import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { Clock, AlertCircle, Check, Loader2 } from 'lucide-react';
import { format, addDays } from 'date-fns';
import DatePickerWithStats from './DatePickerWithStats';

// Effort options with labels
const EFFORT_OPTIONS = [
  { value: 1, label: '<15 mins' },
  { value: 2, label: '15-30 mins' },
  { value: 3, label: '30-60 mins' },
  { value: 5, label: '1-3 hours' },
  { value: 8, label: '3-6 hours' },
  { value: 13, label: '6+ hours' },
];

// Priority options — colours come from the canonical PRIORITY_META CSS
// variables. Never hardcode priority hex here (Feb 2026 token migration).
const PRIORITY_OPTIONS = [
  { value: -1, label: 'P-1', bg: 'var(--orka-priority-emergency)', text: 'var(--orka-text-inverse)' },
  { value: 0,  label: 'P0',  bg: 'var(--orka-priority-fixed)',     text: 'var(--orka-text-inverse)' },
  { value: 1,  label: 'P1',  bg: 'var(--orka-priority-high)',      text: 'var(--orka-text-inverse)' },
  { value: 2,  label: 'P2',  bg: 'var(--orka-priority-medium)',    text: 'var(--orka-text-inverse)' },
  { value: 3,  label: 'P3',  bg: 'var(--orka-priority-low)',       text: 'var(--orka-text-inverse)' },
  { value: 4,  label: 'P4',  bg: 'var(--orka-priority-later)',     text: 'var(--orka-text-inverse)' },
  { value: 5,  label: 'P5',  bg: 'var(--orka-priority-someday)',   text: 'var(--orka-text-inverse)' },
];

// ============ DONE MODAL ============
export const DoneModal = ({ open, onOpenChange, task, onConfirm, saving }) => {
  const isPersonal = task?.work_area_id === 'Personal';
  
  // Initialize selectedEffort from saved actual_effort/task_actual_personal_effort if task is already Done
  const getSavedEffort = () => {
    if (task?.status === 'Done') {
      return isPersonal 
        ? (task.task_actual_personal_effort ?? null)
        : (task.task_actual_effort ?? null);
    }
    return null;
  };
  
  const [selectedEffort, setSelectedEffort] = useState(getSavedEffort());
  const [addFollowup, setAddFollowup] = useState(false);
  const [followupTitle, setFollowupTitle] = useState('');
  const [followupDate, setFollowupDate] = useState(format(addDays(new Date(), 1), 'yyyy-MM-dd'));
  const [followupEffort, setFollowupEffort] = useState(3);
  const [followupPriority, setFollowupPriority] = useState(task?.task_priority ?? 3);
  
  // Update selectedEffort when modal opens with a task that has saved effort
  React.useEffect(() => {
    if (open && task) {
      const savedEffort = isPersonal 
        ? (task.task_actual_personal_effort ?? null)
        : (task.task_actual_effort ?? null);
      if (savedEffort !== null) {
        setSelectedEffort(savedEffort);
      }
    }
  }, [open, task, isPersonal]);

  const handleConfirm = () => {
    if (selectedEffort === null) return;
    
    const updates = {
      status: 'Done',
      [isPersonal ? 'task_actual_personal_effort' : 'task_actual_effort']: selectedEffort
    };
    
    // Add followup task if requested
    if (addFollowup && followupTitle.trim()) {
      updates.followup = {
        title: followupTitle.trim(),
        description: `Followup from: ${task.title}`,
        work_area_id: task.work_area_id || 'General',
        task_estimated_effort: followupEffort,
        task_priority: followupPriority,
        task_delivery_date: followupDate,
        status: 'To Do',
        parent_task_id: task.task_id
      };
    }
    
    onConfirm(updates);
  };

  const handleClose = () => {
    setSelectedEffort(null);
    setAddFollowup(false);
    setFollowupTitle('');
    onOpenChange(false);
  };

  // Colors based on Personal vs Work task
  const accentColor = isPersonal ? 'sky' : 'green';
  const selectedBg = isPersonal ? 'bg-sky-500' : 'bg-green-500';
  const selectedRing = isPersonal ? 'ring-sky-300' : 'ring-green-300';
  const iconColor = isPersonal ? 'text-sky-600' : 'text-green-600';
  const btnBg = isPersonal ? 'bg-sky-600 hover:bg-sky-700' : 'bg-green-600 hover:bg-green-700';

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg z-[9999]" data-testid="done-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className={`h-5 w-5 ${iconColor}`} />
            {isPersonal ? 'Personal Task Complete' : 'Task Complete'}
          </DialogTitle>
        </DialogHeader>
        
        <div className="py-4">
          <p className="text-sm orka-text-secondary text-center mb-4">
            {isPersonal 
              ? 'How much personal effort did you spend on this?'
              : 'How much effort did this take?'
            }
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            {EFFORT_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setSelectedEffort(opt.value)}
                className={`flex flex-col items-center px-4 py-3 rounded-lg orka-transition-colors ${
                  selectedEffort === opt.value 
                    ? `${selectedBg} text-white ring-2 ${selectedRing}` 
                    : 'orka-surface-subtle hover:bg-stone-200 text-stone-700'
                }`}
                data-testid={`effort-${opt.value}`}
              >
                <span className="text-xl font-bold">{opt.value}</span>
                <span className="text-xs mt-1 opacity-80">{opt.label}</span>
              </button>
            ))}
          </div>
          
          {isPersonal && (
            <p className="text-xs text-center orka-text-info font-medium mt-4 orka-bg-info-soft rounded-lg py-2 px-3">
              Personal Effort Tracking — this will be recorded separately from work tasks
            </p>
          )}
          
          {/* Followup Task Section */}
          <div className="mt-6 border-t pt-4">
            <div className="flex items-center gap-2 mb-3">
              <Checkbox 
                id="add-followup" 
                checked={addFollowup} 
                onCheckedChange={setAddFollowup}
                data-testid="add-followup-checkbox"
              />
              <label htmlFor="add-followup" className="text-sm font-medium cursor-pointer">
                Add a followup task
              </label>
            </div>
            
            {addFollowup && (
              <div className="space-y-3 animate-in fade-in slide-in-from-top-2 duration-200">
                <Input
                  value={followupTitle}
                  onChange={(e) => setFollowupTitle(e.target.value)}
                  placeholder="Followup task title..."
                  className="text-sm"
                  data-testid="followup-title"
                />
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Date</label>
                    <DatePickerWithStats
                      value={followupDate}
                      onChange={(val) => setFollowupDate(val)}
                      triggerClassName="h-9 w-full text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Effort</label>
                    <Select value={String(followupEffort)} onValueChange={(v) => setFollowupEffort(Number(v))}>
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {EFFORT_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={String(opt.value)}>{opt.value} - {opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Priority</label>
                    <Select value={String(followupPriority)} onValueChange={(v) => setFollowupPriority(Number(v))}>
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PRIORITY_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={String(opt.value)}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>Cancel</Button>
          <Button 
            onClick={handleConfirm} 
            disabled={selectedEffort === null || saving}
            className={btnBg}
            data-testid="confirm-done"
          >
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
            {isPersonal ? 'Complete Personal Task' : 'Mark as Done'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============ PARTIAL MODAL ============
export const PartialModal = ({ open, onOpenChange, task, onConfirm, saving }) => {
  const [completedEffort, setCompletedEffort] = useState(null);
  const [moveToBacklog, setMoveToBacklog] = useState(false);
  const [newDate, setNewDate] = useState(format(addDays(new Date(), 1), 'yyyy-MM-dd'));
  const [newEffort, setNewEffort] = useState(task?.task_estimated_effort || 3);
  const [newPriority, setNewPriority] = useState(task?.task_priority ?? 3);
  
  const isPersonal = task?.work_area_id === 'Personal';

  // Colors based on Personal vs Work task
  const selectedBg = isPersonal ? 'bg-sky-500' : 'bg-cyan-500';
  const selectedRing = isPersonal ? 'ring-sky-300' : 'ring-cyan-300';
  const iconColor = isPersonal ? 'text-sky-600' : 'text-cyan-600';
  const btnBg = isPersonal ? 'bg-sky-600 hover:bg-sky-700' : 'bg-cyan-600 hover:bg-cyan-700';

  const handleConfirm = () => {
    if (completedEffort === null) return;
    
    const partialData = {
      status: 'Partial',
      [isPersonal ? 'task_actual_personal_effort' : 'task_actual_effort']: completedEffort,
      continuation: {
        title: `${task.title} (Continuation)`,
        description: task.description || '',
        work_area_id: task.work_area_id || 'General',
        task_estimated_effort: newEffort,
        task_priority: newPriority,
        task_delivery_date: moveToBacklog ? null : newDate,
        status: 'To Do',
        start_time: null,
        end_time: null,
        parent_task_id: task.task_id
      }
    };
    
    onConfirm(partialData);
  };

  const handleClose = () => {
    setCompletedEffort(null);
    setMoveToBacklog(false);
    setNewDate(format(addDays(new Date(), 1), 'yyyy-MM-dd'));
    setNewEffort(task?.task_estimated_effort || 3);
    setNewPriority(task?.task_priority ?? 3);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg z-[9999]" data-testid="partial-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className={`h-5 w-5 ${iconColor}`} />
            {isPersonal ? 'Personal Task — Partial Progress' : 'Partial Progress'}
          </DialogTitle>
        </DialogHeader>
        
        <div className="py-4 space-y-6">
          {/* Section 1: Effort Completed */}
          <div>
            <label className="text-sm font-medium orka-text-secondary mb-3 block">
              {isPersonal 
                ? 'How much personal effort was completed so far?'
                : 'How much effort was completed?'
              }
            </label>
            <div className="flex flex-wrap gap-2 justify-center">
              {EFFORT_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setCompletedEffort(opt.value)}
                  className={`flex flex-col items-center px-3 py-2 rounded-lg orka-transition-colors ${
                    completedEffort === opt.value 
                      ? `${selectedBg} text-white ring-2 ${selectedRing}` 
                      : 'orka-surface-subtle hover:bg-stone-200 text-stone-700'
                  }`}
                  data-testid={`partial-effort-${opt.value}`}
                >
                  <span className="text-lg font-bold">{opt.value}</span>
                  <span className="text-xs opacity-80">{opt.label}</span>
                </button>
              ))}
            </div>
            
            {isPersonal && (
              <p className="text-xs text-center orka-text-info font-medium mt-3 orka-bg-info-soft rounded-lg py-2 px-3">
                Personal Effort Tracking — recording partial personal time spent
              </p>
            )}
          </div>

          {/* Section 2: Schedule Continuation */}
          <div className="border-t pt-4">
            <label className="text-sm font-medium orka-text-secondary mb-3 block">
              Schedule continuation task
            </label>
            
            <div className="flex items-center gap-2 mb-4">
              <Checkbox 
                id="moveToBacklog" 
                checked={moveToBacklog} 
                onCheckedChange={setMoveToBacklog}
                data-testid="move-to-backlog-checkbox"
              />
              <label htmlFor="moveToBacklog" className="text-sm orka-text-secondary cursor-pointer">
                Move remainder to Backlog (no date)
              </label>
            </div>

            {!moveToBacklog && (
              <div className="space-y-3 orka-surface-subtle rounded-lg p-3">
                <div>
                  <label className="text-xs font-medium orka-text-tertiary mb-1 block">Reschedule to:</label>
                  <DatePickerWithStats
                    value={newDate}
                    onChange={(val) => setNewDate(val)}
                    triggerClassName="h-9 w-full"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium orka-text-tertiary mb-1 block">Effort remaining:</label>
                    <Select value={String(newEffort)} onValueChange={(v) => setNewEffort(parseInt(v))}>
                      <SelectTrigger className="h-9" data-testid="continuation-effort">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {EFFORT_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={String(opt.value)}>
                            {opt.value} - {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <label className="text-xs font-medium orka-text-tertiary mb-1 block">Priority:</label>
                    <Select value={String(newPriority)} onValueChange={(v) => setNewPriority(parseInt(v))}>
                      <SelectTrigger className="h-9" data-testid="continuation-priority">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PRIORITY_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={String(opt.value)}>
                            <span 
                              className="px-2 py-0.5 rounded text-xs font-bold"
                              style={{ backgroundColor: opt.bg, color: opt.text }}
                            >
                              {opt.label}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>Cancel</Button>
          <Button 
            onClick={handleConfirm} 
            disabled={completedEffort === null || saving}
            className={btnBg}
            data-testid="confirm-partial"
          >
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
            Save & Create Continuation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============ BLOCKED MODAL ============
export const BlockedModal = ({ open, onOpenChange, task, onConfirm, saving }) => {
  const [reason, setReason] = useState(task?.blocked_reason || '');

  const handleConfirm = () => {
    if (!reason.trim()) return;
    
    onConfirm({
      status: 'Blocked',
      blocked_reason: reason.trim()
    });
  };

  const handleClose = () => {
    setReason(task?.blocked_reason || '');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md z-[9999]" data-testid="blocked-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 orka-text-danger" />
            Why is this blocked?
          </DialogTitle>
        </DialogHeader>
        
        <div className="py-4">
          <Textarea 
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Enter the reason why this task is blocked..."
            className="min-h-[100px] resize-none"
            data-testid="blocked-reason-input"
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>Cancel</Button>
          <Button 
            onClick={handleConfirm} 
            disabled={!reason.trim() || saving}
            className="orka-bg-danger hover:bg-red-700"
            data-testid="confirm-blocked"
          >
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export { EFFORT_OPTIONS, PRIORITY_OPTIONS };

// ============ CLOSE PARTIAL MODAL ============
export const ClosePartialModal = ({ open, onOpenChange, task, onConfirm, saving }) => {
  const handleClose = () => {
    onOpenChange(false);
  };

  const handleConfirmClose = () => {
    onConfirm({
      status: 'Done'
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md z-[9999]" data-testid="close-partial-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Check className="h-5 w-5 orka-text-success" />
            Close Partially Completed Task?
          </DialogTitle>
        </DialogHeader>
        
        <div className="py-4 space-y-4">
          <p className="text-sm orka-text-secondary">
            This task was previously marked as partially completed and effort was already recorded. Closing it will mark it as <strong>Done</strong> without capturing additional effort.
          </p>
          
          {task?.continuation_task_id && (
            <div className="orka-bg-info-soft border orka-border-subtle rounded-lg p-3">
              <p className="text-sm orka-text-info">
                A continuation task was created when this was marked partial. You can find and manage it separately.
              </p>
            </div>
          )}
          
          <div className="orka-bg-warning-soft border orka-border-subtle rounded-lg p-3">
            <p className="text-xs orka-text-warning">
              Note: A partially completed task cannot be changed back to "To Do". If more work is needed, use the continuation task instead.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>Cancel</Button>
          <Button 
            onClick={handleConfirmClose} 
            disabled={saving}
            className="orka-bg-success hover:bg-emerald-700"
            data-testid="confirm-close-partial"
          >
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />}
            Close Task
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
